import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, Pin, Search, Plus, ChevronLeft, ChevronRight, Paperclip } from 'lucide-react';
import { useAnnouncements, type AnnouncementListItem } from '@/lib/queries';
import { useAuthStore } from '@/store/auth.store';
import { initials } from '@isoft-people/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageTransition, FadeIn } from '@/components/ui/motion';

const AUDIENCE_LABELS: Record<string, string> = {
  ALL: 'Todos',
  BY_COUNTRY: 'Por país',
  BY_DEPARTMENT: 'Por departamento',
  BY_COUNTRY_AND_DEPARTMENT: 'País y depto.',
};

function timeAgo(dateStr: string) {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `Hace ${days}d`;
  return d.toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function AnnouncementsPage() {
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'ADMIN' || role === 'HR';

  const [statusFilter, setStatusFilter] = useState('PUBLISHED');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useAnnouncements({
    status: isAdmin ? statusFilter : undefined,
    search: search || undefined,
    page,
    pageSize: 20,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 20);
  const pinned = items.filter((a) => a.pinned);
  const regular = items.filter((a) => !a.pinned);

  return (
    <PageTransition className="flex flex-col gap-6">
      {/* Header con acento de marca */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-subtle">
            <Megaphone size={22} className="text-primary" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-font-primary">Anuncios</h1>
            <p className="mt-0.5 text-sm text-font-secondary">
              Comunicados y avisos internos de ISOFT
            </p>
          </div>
        </div>
        {isAdmin && (
          <Button asChild variant="gradient" size="lg">
            <Link to="/anuncios/nuevo">
              <Plus size={16} /> Nuevo anuncio
            </Link>
          </Button>
        )}
      </div>

      {/* Filtros */}
      <FadeIn delay={0.05}>
        <div className="flex flex-wrap items-center gap-3">
          {isAdmin && (
            <div className="flex rounded-md border border-border bg-white p-1">
              {(['PUBLISHED', 'DRAFT', 'ARCHIVED'] as const).map((s) => (
                <Button
                  key={s}
                  variant={statusFilter === s ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => {
                    setStatusFilter(s);
                    setPage(1);
                  }}
                  className="h-8 px-4 text-xs"
                >
                  {s === 'PUBLISHED' ? 'Publicado' : s === 'DRAFT' ? 'Borrador' : 'Archivado'}
                </Button>
              ))}
            </div>
          )}
          <div className="relative flex-1">
            <div className="flex h-10 w-full max-w-sm items-center gap-2 rounded-md border border-border bg-white px-3 transition focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
              <Search size={15} className="text-font-tertiary" />
              <input
                type="text"
                placeholder="Buscar anuncio..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="flex-1 bg-transparent text-sm text-font-primary placeholder:text-font-tertiary focus:outline-none"
              />
            </div>
          </div>
        </div>
      </FadeIn>

      {isLoading && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-xl border border-border brand-shimmer" />
          ))}
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <FadeIn delay={0.1}>
          <div className="flex flex-col items-center gap-3 py-16">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-bg">
              <Megaphone size={24} className="text-font-tertiary" />
            </span>
            <p className="text-sm text-font-tertiary">Sin anuncios por el momento.</p>
          </div>
        </FadeIn>
      )}

      {/* Fijados */}
      {pinned.length > 0 && (
        <FadeIn delay={0.08}>
          <div className="rounded-xl bg-primary-subtle p-5">
            <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary">
              <Pin size={12} /> Fijados
            </p>
            <div className="grid grid-cols-1 gap-4 stagger md:grid-cols-2">
              {pinned.map((a) => (
                <AnnouncementCard key={a.id} announcement={a} compact />
              ))}
            </div>
          </div>
        </FadeIn>
      )}

      {/* Lista regular */}
      {regular.length > 0 && (
        <div className="space-y-3 stagger">
          {regular.map((a) => (
            <AnnouncementCard key={a.id} announcement={a} />
          ))}
        </div>
      )}

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-font-secondary">
          <span>
            Mostrando {(page - 1) * 20 + 1}–{Math.min(page * 20, total)} de {total}
          </span>
          <div className="flex flex-wrap items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-font-secondary transition hover:bg-bg hover:text-primary disabled:opacity-40"
            >
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`flex h-8 w-8 items-center justify-center rounded-md text-xs font-semibold transition ${
                  p === page
                    ? 'bg-primary text-white shadow-sm'
                    : 'border border-border bg-white text-font-secondary hover:bg-bg'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-font-secondary transition hover:bg-bg hover:text-primary disabled:opacity-40"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </PageTransition>
  );
}

function AnnouncementCard({
  announcement: a,
  compact,
}: {
  announcement: AnnouncementListItem;
  compact?: boolean;
}) {
  const authorName = a.createdBy?.employee
    ? `${a.createdBy.employee.firstName} ${a.createdBy.employee.lastName}`
    : a.createdBy?.email ?? '–';
  const authorPhoto = a.createdBy?.employee?.photoUrl;
  const [failedImg, setFailedImg] = useState<Set<string>>(new Set());
  const images = (a.attachments ?? []).filter(
    (m) => m.kind === 'image' && !failedImg.has(m.s3Key),
  );
  const files = (a.attachments ?? []).filter((m) => m.kind !== 'image');

  return (
    <Link
      to={`/anuncios/${a.id}`}
      className={`block rounded-xl border border-border bg-white transition hover:border-primary hover:shadow-sm lift ${
        compact ? 'p-4' : 'p-5'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Indicador de no leído */}
        {!a.isRead && (
          <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
        )}
        {a.isRead && <span className="mt-2 h-2 w-2 flex-shrink-0" />}

        <div className="flex-1 min-w-0">
          <h3
            className={`font-semibold text-font-primary ${
              compact ? 'text-sm' : 'text-base'
            } truncate`}
          >
            {a.pinned && <Pin size={12} className="mr-1 inline text-primary" />}
            {a.title}
          </h3>
          <p className="mt-1 line-clamp-2 text-sm text-font-secondary">{a.body}</p>

          {/* Preview de imágenes estilo feed */}
          {images.length > 0 && (
            <div className="mt-3 flex gap-1.5">
              {images.slice(0, 4).map((img, i) => (
                <div
                  key={img.s3Key}
                  className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg border border-border"
                >
                  <img
                    src={img.url}
                    alt=""
                    loading="lazy"
                    onError={() =>
                      setFailedImg((prev) => {
                        const next = new Set(prev);
                        next.add(img.s3Key);
                        return next;
                      })
                    }
                    className="h-full w-full object-cover"
                  />
                  {i === 3 && images.length > 4 && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/55 text-sm font-bold text-white">
                      +{images.length - 4}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {files.length > 0 && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-font-tertiary">
              <Paperclip size={12} /> {files.length}{' '}
              {files.length === 1 ? 'documento adjunto' : 'documentos adjuntos'}
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-3 gap-y-1">
            {authorPhoto ? (
              <img
                src={authorPhoto}
                alt={authorName}
                className="h-6 w-6 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-white">
                {initials(authorName)}
              </div>
            )}
            <span className="text-xs text-font-secondary">{authorName}</span>
            <span className="text-xs text-font-tertiary">·</span>
            <span className="text-xs text-font-tertiary">
              {timeAgo(a.publishedAt ?? a.createdAt)}
            </span>
            <Badge variant="info" className="ml-auto">
              {AUDIENCE_LABELS[a.audienceType] ?? a.audienceType}
            </Badge>
            {a.status === 'DRAFT' && (
              <Badge variant="warning" dot>
                Borrador
              </Badge>
            )}
            {a.status === 'ARCHIVED' && (
              <Badge variant="neutral" dot>
                Archivado
              </Badge>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
